/**
 * QuickActions Component
 * Quick action buttons for common tasks in the FinList journey
 */

import React from 'react';
import { Plus, TrendingDown, TrendingUp, ShoppingCart, Target, ListTodo, Edit3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CATEGORY_TYPES } from '@/constants/categoryTaxonomy';
import { track } from '@/services/analytics';

export default function QuickActions({ onAddExpense, onAddIncome }) {
  const navigate = useNavigate();

  const openTransactionDialog = (mode = CATEGORY_TYPES.EXPENSE) => {
    track('transaction_modal_opened', {
      source: 'dashboard_quick_actions',
      mode,
      stage: 'trigger'
    }).catch(() => {});

    if (mode === CATEGORY_TYPES.INCOME) {
      onAddIncome?.();
      return;
    }

    onAddExpense?.();
  };

  const shoppingListAction = {
    id: 'shopping-list',
    label: 'Shopping List',
    description: 'Create & manage',
    icon: ShoppingCart,
    color: '#06D6A0',
    onClick: () => navigate('/shopping-lists')
  };

  const goalAction = {
    id: 'set-goal',
    label: 'Set Goal',
    description: 'Save for something',
    icon: Target,
    color: '#118AB2',
    onClick: () => navigate('/savings-goals')
  };

  const transactionAction = {
    id: 'add-transaction',
    label: 'Add Expense',
    description: 'Log an expense',
    icon: TrendingDown,
    color: '#EF4444',
    onClick: () => openTransactionDialog(CATEGORY_TYPES.EXPENSE)
  };

  const incomeAction = {
    id: 'add-income',
    label: 'Add Income',
    description: 'Log income',
    icon: TrendingUp,
    color: '#06D6A0',
    onClick: () => openTransactionDialog(CATEGORY_TYPES.INCOME)
  };

  const manageTransactionsAction = {
    id: 'manage-transactions',
    label: 'Transactions',
    description: 'Edit or delete',
    icon: Edit3,
    color: '#8B5CF6',
    onClick: () => navigate('/premium/transactions')
  };

  const taskAction = {
    id: 'add-task',
    label: 'Add Task',
    description: 'Stay on top of bills',
    icon: ListTodo,
    color: '#FF9F1C',
    onClick: () => navigate('/tasks')
  };

  const actions = [transactionAction, incomeAction, manageTransactionsAction, shoppingListAction, taskAction, goalAction];

  const renderActionCard = (action, animationDelay = 0) => {
    const Icon = action.icon;
    return (
      <motion.button
        key={action.id}
        whileHover={{ scale: 1.02, y: -2 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: animationDelay }}
        onClick={action.onClick}
        className="flex flex-col items-center justify-center gap-1.5 sm:gap-2 p-3 sm:px-5 sm:py-5 bg-white dark:bg-slate-800 rounded-xl hover:shadow-lg dark:hover:shadow-slate-900 transition-all duration-200 shadow-sm group touch-manipulation h-full min-h-[100px] sm:min-h-[120px]"
        style={{
          borderWidth: '2px',
          borderColor: action.color
        }}
      >
        <div
          className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
          style={{
            backgroundColor: `${action.color}15`
          }}
        >
          <Icon
            size={20}
            className="sm:w-6 sm:h-6"
            style={{ color: action.color }}
          />
        </div>
        <div className="text-center min-w-0 w-full overflow-hidden">
          <div className="flex items-center justify-center gap-0.5 sm:gap-1">
            <Plus
              size={12}
              className="sm:w-4 sm:h-4 flex-shrink-0"
              style={{ color: action.color }}
            />
            <span
              className="font-bold text-xs sm:text-base leading-tight"
              style={{ color: action.color }}
            >
              {action.label}
            </span>
          </div>
          <div className="text-[9px] sm:text-xs font-normal text-gray-600 dark:text-gray-400 mt-0.5 leading-tight truncate">
            {action.description}
          </div>
        </div>
      </motion.button>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="flex justify-center px-2"
    >
      <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4 w-full max-w-6xl auto-rows-fr">
        {actions.map((action, index) => renderActionCard(action, index * 0.1))}
      </div>
    </motion.div>
  );
}
